import { Users } from 'lucide-react';

import type { NivelDeAcesso, UsuarioResumo } from '@/api/client';
import { cn } from '@/lib/utils';

/**
 * "você e Dr. Fulano", "Dr. Fulano", "você, Dr. A e mais 2" (depois de "No acervo de:"). O
 * backend põe quem consulta primeiro quando a linha é dele (``acesso === 'DONO'``) — é assim
 * que sai o "você".
 */
export function nomesDoSelo(advogados: UsuarioResumo[], euPrimeiro: boolean, max = 2): string {
  const nomes = advogados.map((a, i) => {
    if (euPrimeiro && i === 0) return 'você';
    const nome = a.nome || 'Colega';
    return a.ex_membro ? `${nome} (ex-membro)` : nome;
  });
  if (nomes.length <= max) {
    return nomes.length === 2 ? `${nomes[0]} e ${nomes[1]}` : nomes.join(', ');
  }
  const resto = nomes.length - max;
  return `${nomes.slice(0, max).join(', ')} e mais ${resto}`;
}

interface SeloDeAdvogadosProps {
  advogados?: UsuarioResumo[];
  acesso?: NivelDeAcesso;
  className?: string;
}

/**
 * O selo da visão do escritório (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md A7): quem tem
 * este processo no acervo, e se quem vê só pode ler. Some quando o processo é só do
 * usuário — no acervo de quem não tem colegas nada muda. O rótulo é explícito porque fica
 * na mesma linha do "Cuida: …" da distribuição (§19): só os nomes liam como "os dois cuidam".
 */
export function SeloDeAdvogados({ advogados, acesso, className }: SeloDeAdvogadosProps) {
  if (!advogados || advogados.length === 0) return null;
  const euPrimeiro = (acesso ?? 'DONO') === 'DONO';
  const nomes = nomesDoSelo(advogados, euPrimeiro);
  const todos = nomesDoSelo(advogados, euPrimeiro, advogados.length);
  return (
    <span
      className={cn('inline-flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground', className)}
      title={`No acervo de: ${todos}`}
    >
      <Users className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">No acervo de: {nomes}</span>
      {acesso === 'LEITURA' && (
        <span className="shrink-0 rounded bg-muted px-1 py-px text-[11px] font-medium">só leitura</span>
      )}
    </span>
  );
}
